import React from "react";
import { Box, Tooltip } from "@twilio-paste/core";
import { ConversationDto } from "../../../dto/conversation";
import { formatDateTime, hasWarningForConversation } from "./helpers";
import { AvatarColumn } from "./AvatarColumn";
import { MetaInfo } from "./MetaInfo";
import { Snippet } from "./Snippet";
import { TitleRow } from "./TitleRow";
import { ConversationBadge } from "./ConversationBadge";
import { useConversationLockState } from "../../../hooks/useConversationLockState";

export type ConversationListItemProps = {
  convo: ConversationDto;
  onClick?: (convo: ConversationDto) => void;
  isSelected?: boolean;

  myOperatorId: string | null;
  isSupervisor: boolean;
};

export const ConversationListItem: React.FC<ConversationListItemProps> = ({
  convo,
  onClick,
  isSelected = false,
  myOperatorId,
  isSupervisor,
}) => {
  const warning = hasWarningForConversation(convo);
  const dateLabel = formatDateTime(convo.lastMessageAt ?? convo.createdAt);

  const operatorLabel = convo.operatorName ?? undefined;

  const isAvailable = !convo.lockedByOperatorId;
  const customers = convo.customers ?? [];
  const customer = customers[0];
  const avatarUrl = (customer as any)?.avatarUrl ?? undefined;

  const unreadCount = convo.unreadMessages ?? 0;
  const hasUnread = unreadCount > 0;
  const showTransferBadge = !!convo.showTransferBadge;
  const unreadLabel =
    unreadCount > 99 ? "99+" : hasUnread ? unreadCount.toString() : "";

  const { canOpen, isLockedForMe, lockDisabled, lockTooltip } =
    useConversationLockState({ convo, myOperatorId, isSupervisor });

  const handleClick = () => {
    if (!canOpen) return;
    onClick?.(convo);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (!canOpen) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(convo);
    }
  };

  return (
    <Box
      as="article"
      padding="space40"
      borderBottomWidth="borderWidth10"
      borderBottomStyle="solid"
      borderBottomColor="colorBorderWeak"
      backgroundColor={
        isSelected ? "colorBackgroundStrong" : "colorBackgroundBody"
      }
      _hover={{
        backgroundColor: isSelected
          ? "colorBackgroundStrong"
          : canOpen
          ? "colorBackgroundSubaccount"
          : "colorBackgroundBody",
      }}
      maxHeight="130px"
      opacity={canOpen ? 1 : 0.75}
      cursor={canOpen ? "pointer" : "not-allowed"}
      role="button"
      aria-disabled={!canOpen}
      tabIndex={canOpen ? 0 : -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Box display="flex" marginBottom="space50">
        <Box position="relative" marginRight="space20">
          <AvatarColumn imageUrl={avatarUrl} alt="conversation" />

          <ConversationBadge
            unreadCount={unreadCount}
            showTransferBadge={showTransferBadge}
          />
        </Box>

        <Box flex="1 1 auto">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            marginLeft="space30"
          >
            <Box flex="1 1 auto" minWidth={0} maxWidth={170}>
              <TitleRow phone={convo.customerPhone} customers={customers} />
            </Box>

            <MetaInfo
              dateLabel={dateLabel}
              operatorLabel={operatorLabel}
              isAvailable={isAvailable}
              lockDisabled={lockDisabled}
              lockTooltip={lockTooltip}
            />
          </Box>
        </Box>
      </Box>

      <Snippet text={convo.lastMessageText} />
    </Box>
  );
};
